/**
 * Intl formatting bound to the active locale. Nothing formats a date, a number or a name by
 * string concatenation — that is how a Norwegian date silently becomes an American one.
 */
import { DEFAULT_LOCALE, type Locale } from '@tvitvi/shared';

const INTL_TAG: Record<Locale, string> = { nb: 'nb-NO', en: 'en-GB' };

export function intlTag(locale: Locale): string {
  return INTL_TAG[locale];
}

/**
 * EN DATO SOM IKKE ER EN DATO SKAL SE UT SOM EN TOM DATO — IKKE VELTE SIDA.
 *
 * ---------------------------------------------------------------------------------------------
 * TREDJE GANGEN SAMME FEILFORM (N1, 20. september; A2 dagen før; K-1-porten natta før det).
 *
 * `Intl.DateTimeFormat.format` SVARER IKKE TOMT på en ugyldig dato — den kaster
 * `RangeError: Invalid time value`. Kastet skjer under render, så det er ikke feltet som blir rart:
 * det er hele sida som faller til «Noe gikk galt». I N1 holdt det å skrive «1.7.202» på vei mot
 * «1.7.2027» i hurtiglegg.
 *
 * Hver av de tre gangene ble kilden rettet — parseren, `time-in-zone`, kolonnen — og hver gang sto
 * neste kilde igjen utrettet. DERFOR SITTER SJEKKEN HER, i det ene laget alle tre gikk gjennom, og
 * ikke på kallstedene. Det finnes rundt 300 kall til disse fem funksjonene; sjekken skal ikke være
 * noe 300 steder må huske.
 *
 * Kilden rettes fortsatt — dette gjør bare at neste gang én slipper unna, blir det en tom celle og
 * ikke et hvitt skjermbilde. `null`, `undefined` og `''` gir den samme streken, fordi «ingen dato»
 * og «en dato ingen kan lese» er det samme for den som ser på.
 */
/**
 * Eksportert siden 21. september (QA13 runde 2, 28b og J6): flere lister enn datoene trenger den
 * samme streken for en tom verdi, og en andre kopi av tegnet er et andre tegn som kan drifte.
 */
export const NO_VALUE = '—';

/**
 * En dato-streng → `Date`, eller `null` hvis den ikke er en dato.
 *
 * Eksportert fordi de fem funksjonene under ikke er de eneste som formaterer: rundt tjue steder
 * lager sin egen `Intl.DateTimeFormat` fordi de trenger en form ingen av de fem har — bare klokka i
 * en sone, bare ukedagen, `en-CA` som nøkkel. Alle de stedene skal bruke DENNE til å slippe unna,
 * så sjekken er den samme sjekken og ikke tjue varianter av den.
 */
export function instantOf(value: string | null | undefined): Date | null {
  if (!value) return null;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/**
 * Formater med en formatter noen andre har laget — trygt.
 *
 * De tjue stedene som lager sin egen `Intl.DateTimeFormat` gjør det fordi de trenger en form de fem
 * under ikke har, og fordi formatteren er dyr å lage og derfor lages én gang utenfor løkka. Denne
 * gir dem den samme veien ut uten å ta fra dem formatteren: `formatWith(clock, iso)` i stedet for
 * `clock.format(new Date(iso))`.
 */
export function formatWith(
  formatter: Intl.DateTimeFormat,
  value: string | null | undefined,
  fallback: string = NO_VALUE,
): string {
  const instant = instantOf(value);
  return instant === null ? fallback : formatter.format(instant);
}

export function formatDate(value: string | null | undefined, locale: Locale): string {
  const instant = instantOf(value);
  if (instant === null) return NO_VALUE;
  return new Intl.DateTimeFormat(intlTag(locale), { dateStyle: 'medium' }).format(instant);
}

export function formatDateTime(value: string | null | undefined, locale: Locale): string {
  const instant = instantOf(value);
  if (instant === null) return NO_VALUE;
  return new Intl.DateTimeFormat(intlTag(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(instant);
}

/**
 * The same, read on the wall clock of a NAMED place rather than of the machine.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY A SECOND FORMATTER RATHER THAN AN ARGUMENT ON THE FIRST (decision 0016, answer C).
 *
 * `formatDateTime` above is right for everything whose moment belongs to the READER: a message
 * timestamp, an audit line, "sist endret". A set time does not belong to the reader — it belongs
 * to the field the stage is standing in, and `editions.timezone` is that place. A booker in Berlin
 * reading an Oslo running order must see the time the artist walks on, not the time it is in
 * Berlin when they do.
 *
 * The two are therefore two different questions, and a table that answered them with one function
 * and an optional argument would be one forgotten argument away from silently answering the wrong
 * one. `lib/zoned-time.ts` makes the same split for the inverse direction, and its header carries
 * the whole argument.
 *
 * Nothing here is hand-rolled: `Intl` carries the IANA database, so this is `formatDateTime` with
 * the zone named rather than implied.
 * ---------------------------------------------------------------------------------------------
 */
export function formatDateTimeInZone(
  value: string | null | undefined,
  locale: Locale,
  timeZone: string,
): string {
  const instant = instantOf(value);
  if (instant === null) return NO_VALUE;
  return new Intl.DateTimeFormat(intlTag(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(instant);
}

/**
 * «DATO UTEN KLOKKESLETT» (215.15): en reise eller et måltid som bare har en dag, lagret som
 * midnatt i årgangens sone og flagget. Da vises dagen alene — «00:00» ville vært en tid ingen skrev.
 * Uten flagget er det `formatDateTimeInZone`, også for en ekte nattreise 00:00.
 */
export function formatWhenInZone(
  value: string | null | undefined,
  dateOnly: boolean | undefined,
  locale: Locale,
  timeZone: string,
): string {
  if (dateOnly !== true) return formatDateTimeInZone(value, locale, timeZone);
  const instant = instantOf(value);
  if (instant === null) return NO_VALUE;
  return new Intl.DateTimeFormat(intlTag(locale), { dateStyle: 'medium', timeZone }).format(
    instant,
  );
}

/**
 * JUST THE CLOCK, in the given zone — «10:00».
 *
 * Its use is the SECOND half of a span: «15. juli 2027, 10:00–12:00». The day is already said by
 * the first half, and repeating it there is how a row becomes twice as long as the fact in it.
 *
 * Moved here from `ShiftTimeline` on 16 September (QA-funn 10 fra trinn 1): the resource panel
 * showed a start with no end, and a second private copy of four lines of `Intl` would have been
 * two places to get the zone wrong.
 */
export function formatClockInZone(
  value: string | null | undefined,
  locale: Locale,
  timeZone: string,
): string {
  const instant = instantOf(value);
  if (instant === null) return NO_VALUE;
  return new Intl.DateTimeFormat(intlTag(locale), {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(instant);
}

/**
 * A CALENDAR DATE, which is a third question and not a shorter version of the first two.
 *
 * ---------------------------------------------------------------------------------------------
 * `formatDate` ABOVE IS WRONG FOR THESE, AND WRONG QUIETLY.
 *
 * It puts its argument through `new Date(value)`, and `new Date('2032-01-01')` is parsed as UTC
 * MIDNIGHT — then formatted on the reader's own clock. In Oslo that is 01:00 on the 1st and reads
 * correctly; in New York it is 19:00 on 31 December and reads as the day before. Every reader west
 * of Greenwich sees a hotel booked one night early, and nothing anywhere reports an error.
 *
 * That is fine for what `formatDate` is actually given — instants, whose moment belongs to the
 * reader — and it is exactly wrong for a `date` column. `nights_from` has no clock and no zone: a
 * hotel night is the 14th in Trondheim and the 14th in Auckland, because it is a page in a
 * calendar rather than a moment in time. So the value is read back in the zone it was written in,
 * which for a zoneless date is UTC — the identity that makes the day survive the round trip.
 *
 * Three formatters for three questions, none of them reachable by forgetting an argument to
 * another: the reader's moment (`formatDateTime`), the event's moment (`formatDateTimeInZone`),
 * and the day that is the same everywhere (this one).
 * ---------------------------------------------------------------------------------------------
 */
export function formatCalendarDate(value: string | null | undefined, locale: Locale): string {
  const instant = instantOf(value);
  if (instant === null) return NO_VALUE;
  return new Intl.DateTimeFormat(intlTag(locale), {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(instant);
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(intlTag(locale)).format(value);
}

/**
 * Øre to «45 000 kr» (Økonomi [F2]).
 *
 * HERE RATHER THAN IN THE COMPONENT, for exactly the reason `formatList` is: where the currency
 * goes, which separator groups the thousands and whether the minus sign leads or trails are
 * properties of the LANGUAGE. Norwegian puts «kr» after the number with a non-breaking space;
 * English puts it first. `Intl` knows both, and a component that concatenated would get one of
 * them wrong in a way no reviewer of that component would notice.
 *
 * MINOR UNITS IN, and the division is the last thing that happens: `amountMinor / 100` is exact
 * for every value the API can send, because `MAX_COST_AMOUNT_MINOR` keeps a line four orders of
 * magnitude inside `Number.MAX_SAFE_INTEGER`.
 *
 * NO DECIMALS BY DEFAULT. A festival budget is read in whole kroner — «45 000 kr», not
 * «45 000,00 kr» — and the øre matter on an invoice line rather than on a summary. Pass
 * `decimals: true` where they do.
 */
export function formatMoney(
  amountMinor: number,
  currency: string,
  locale: Locale,
  options: { decimals?: boolean } = {},
): string {
  const digits = options.decimals === true ? 2 : 0;
  return new Intl.NumberFormat(intlTag(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amountMinor / 100);
}

/**
 * `["frist","ansvarlig"]` → `"frist og ansvarlig"` / `"deadline and assignee"`.
 *
 * Here for the same reason the date formatters are: the separator before the last item is a
 * property of the language, not punctuation a component may pick. `join(', ')` is right in
 * neither locale and looks right in both, which is how it survives review.
 */
export function formatList(items: readonly string[], locale: Locale): string {
  return new Intl.ListFormat(intlTag(locale), { style: 'long', type: 'conjunction' }).format(items);
}

/**
 * A label written to stand alone, put into the middle of a sentence — and back again at the front.
 *
 * `performances.travel.mode.flight` is «Fly», because that is how it is written on its own in a
 * filter and in a form. Joined by `formatList` it becomes «Buss og Fly», with a capital in the
 * middle of a phrase that nobody writes that way. Lowercasing each part and capitalising the
 * result gives «Buss og fly», which is the same sentence a person would type.
 *
 * `toLocaleLowerCase` rather than `toLowerCase`: the difference only shows up in languages Tvitvi
 * does not have yet, and the habit costs nothing.
 */
export function sentenceList(items: readonly string[], locale: Locale): string {
  const tag = intlTag(locale);
  const phrase = formatList(
    items.map((item) => item.toLocaleLowerCase(tag)),
    locale,
  );
  return phrase === '' ? '' : phrase.charAt(0).toLocaleUpperCase(tag) + phrase.slice(1);
}

/** Two-letter monogram for an avatar placeholder. Grapheme-safe enough for Latin scripts. */
export function initials(displayName: string): string {
  /* Bare ord som begynner med en bokstav: «Control Room (test)» ga «C(» (QA6 K6, L2). */
  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter((part) => /^\p{L}/u.test(part));
  const first = parts.at(0)?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

/**
 * Stable categorical colour for org-defined data (principle 9): departments, stages, tags.
 * The colour is derived from the entity id, never hardcoded per organization.
 */
export function categoricalColorVar(id: string): string {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  }
  return `var(--color-cat-${(hash % 8) + 1})`;
}

/**
 * Stor forbokstav på et ord som kommer fra en kilde som ikke bryr seg om det.
 *
 * Sjangrene er importert fra Greenroom i små bokstaver — «rock», «hiphop», «elektronisk» — og en
 * chip som sier «rock» ser ut som en feil ved siden av «Band» og «Venter svar». VERDIEN ENDRES
 * IKKE: filteret, sorteringen og lagringen bruker fortsatt kildens egen streng, og bare visningen
 * får den store bokstaven. Å rette dataene ville gjort «Rock» og «rock» til to sjangre.
 *
 * `toLocaleUpperCase` framfor `toUpperCase`: en tyrkisk «i» er ikke en engelsk «I», og bokstaven
 * her kommer fra et navn ingen av oss har skrevet.
 */
export function capitalised(value: string): string {
  const first = value.charAt(0);
  return first === '' ? value : `${first.toLocaleUpperCase(DEFAULT_LOCALE)}${value.slice(1)}`;
}
